import React, { useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { HiCalendarDays, HiTrophy } from "react-icons/hi2";
import { SessionVM } from "./scheduleTypes";
import { SessionStateChip } from "./SessionCard";
import { EventVM, eventDayIndexForKey } from "./eventVM";
import RecoveryScheduleCard from "./RecoveryScheduleCard";
import { ScheduledRecovery } from "../../types/scheduledRecovery";
import PerformanceIntentBadge from "../shared/PerformanceIntentBadge";
import { ColorMap, resolveTrainingTypeColor } from "../../utils/scheduleColors";
import {
  addDays,
  getLocalDateString,
  isSameDay,
  startOfToday,
} from "./scheduleDateUtils";

const LONG_PRESS_MS = 450;

interface WeekViewProps {
  weekStart: Date;
  getSessionsForDate: (date: Date) => SessionVM[];
  /** Events on a day (Events.md §4). */
  getEventsForDate?: (date: Date) => EventVM[];
  isEventLogged?: (eventId: string) => boolean;
  onSelectEvent?: (eventId: string) => void;
  getRecoveriesForDate?: (date: Date) => ScheduledRecovery[];
  onSelectRecovery?: (item: ScheduledRecovery) => void;
  trainingTypeColors?: ColorMap;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onAssign: (date: Date) => void;
  onSelectSession: (id: string) => void;
  /** Desktop drag-and-drop between day rows. */
  onReschedule: (sessionId: string, newDate: string) => void;
  /** Whether the session may move (drag handle / long-press / calendar button). */
  canChangeDate: (id: string) => boolean;
  /** Touch path: long-press or the calendar button opens the date sheet. */
  onChangeDate: (id: string) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  weekStart,
  getSessionsForDate,
  getEventsForDate,
  isEventLogged,
  onSelectEvent,
  getRecoveriesForDate,
  onSelectRecovery,
  trainingTypeColors,
  onPrevWeek,
  onNextWeek,
  onToday,
  onAssign,
  onSelectSession,
  onReschedule,
  canChangeDate,
  onChangeDate,
}) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  // Long-press (touch) → Change date. The timer is cancelled on move/up and
  // a fired long-press suppresses the click that follows it.
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const startLongPress = (e: React.PointerEvent, sessionId: string) => {
    if (e.pointerType !== "touch" || !canChangeDate(sessionId)) return;
    clearLongPress();
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      longPressTimer.current = null;
      onChangeDate(sessionId);
    }, LONG_PRESS_MS);
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = startOfToday();

  const rangeLabel = `${weekStart.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  })} – ${days[6].toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  })}`;

  return (
    <div>
      {/* Period navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onPrevWeek}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-base-300/60 text-base-content hover:bg-base-300 transition-colors"
          aria-label="Previous week"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h3 className="text-base font-semibold text-base-content font-grotesk">
          {rangeLabel}
        </h3>
        <button
          onClick={onNextWeek}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-base-300/60 text-base-content hover:bg-base-300 transition-colors"
          aria-label="Next week"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="mt-2 text-center">
        <button
          onClick={onToday}
          className="text-sm font-medium text-primary hover:text-primary/80 font-grotesk"
        >
          Today
        </button>
      </div>

      <div className="mt-3 divide-y divide-base-300">
        {days.map((day) => {
          const dateStr = getLocalDateString(day);
          const sessions = getSessionsForDate(day);
          const events = getEventsForDate?.(day) ?? [];
          const recoveries = getRecoveriesForDate?.(day) ?? [];
          const isToday = isSameDay(day, today);
          const isDropTarget = dropTarget === dateStr && draggingId;

          return (
            <div
              key={dateStr}
              onDragOver={(e) => {
                if (!draggingId) return;
                e.preventDefault();
                setDropTarget(dateStr);
              }}
              onDragLeave={() =>
                setDropTarget((prev) => (prev === dateStr ? null : prev))
              }
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData("text/plain");
                setDropTarget(null);
                setDraggingId(null);
                if (id) onReschedule(id, dateStr);
              }}
              className={`flex items-stretch gap-3 py-3 ${
                isDropTarget ? "bg-primary/5 rounded-xl" : ""
              }`}
            >
              {/* Date badge */}
              <div
                className={`flex w-11 flex-shrink-0 flex-col items-center justify-center ${
                  isToday ? "text-primary" : "text-base-content/70"
                }`}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide font-grotesk">
                  {day.toLocaleDateString("en-GB", { weekday: "short" })}
                </span>
                <span
                  className={`text-xl font-semibold font-geist tabular-nums ${
                    isToday ? "text-primary" : "text-base-content"
                  }`}
                >
                  {day.getDate()}
                </span>
              </div>

              {/* Events, then sessions / assign placeholder */}
              <div className="flex-1 min-w-0 space-y-2">
                {recoveries.map((item) => (
                  <RecoveryScheduleCard
                    key={item.id}
                    item={item}
                    onClick={
                      onSelectRecovery ? () => onSelectRecovery(item) : undefined
                    }
                  />
                ))}
                {events.map((comp) => {
                  const dayIndex = eventDayIndexForKey(comp, dateStr);
                  return (
                    <div
                      key={`comp-${comp.id}`}
                      onClick={() => onSelectEvent?.(comp.id)}
                      role={onSelectEvent ? "button" : undefined}
                      className={`flex items-center gap-2 rounded-xl border border-base-300 bg-base-200/60 hover:bg-base-200 px-3 py-2.5 transition-colors select-none ${
                        onSelectEvent ? "cursor-pointer" : ""
                      }`}
                    >
                      <HiTrophy
                        className="h-4 w-4 flex-shrink-0"
                        style={{ color: comp.color }}
                      />
                      <span className="flex-1 truncate text-sm font-medium text-base-content font-grotesk">
                        {comp.name}
                      </span>
                      {dayIndex && (
                        <span className="text-xs text-base-content/50 font-geist tabular-nums">
                          Day {dayIndex}
                        </span>
                      )}
                      <PerformanceIntentBadge type={comp.type} />
                      {isEventLogged?.(comp.id) && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk bg-success/15 text-success">
                          Logged
                        </span>
                      )}
                    </div>
                  );
                })}
                {sessions.length === 0 && (events.length > 0 || recoveries.length > 0) ? (
                  <button
                    onClick={() => onAssign(day)}
                    className="w-full min-h-9 rounded-xl border border-dashed border-base-300 py-1.5 text-xs text-base-content/40 hover:border-base-content/30 hover:text-base-content/60 transition-colors font-grotesk"
                  >
                    + Assign session
                  </button>
                ) : sessions.length === 0 ? (
                  <button
                    onClick={() => onAssign(day)}
                    className="w-full min-h-11 rounded-xl border border-dashed border-base-300 py-2.5 text-sm text-base-content/40 hover:border-base-content/30 hover:text-base-content/60 transition-colors font-grotesk"
                  >
                    + Assign session
                  </button>
                ) : (
                  sessions.map((session) => {
                    const color = resolveTrainingTypeColor(
                      session.typeColorKey,
                      trainingTypeColors,
                    );
                    const movable = canChangeDate(session.id);
                    return (
                      <div
                        key={session.id}
                        draggable={movable}
                        onDragStart={(e) => {
                          if (!movable) return;
                          e.dataTransfer.setData("text/plain", session.id);
                          e.dataTransfer.effectAllowed = "move";
                          setDraggingId(session.id);
                        }}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDropTarget(null);
                        }}
                        onPointerDown={(e) => startLongPress(e, session.id)}
                        onPointerMove={clearLongPress}
                        onPointerUp={clearLongPress}
                        onPointerCancel={clearLongPress}
                        onPointerLeave={clearLongPress}
                        onContextMenu={(e) => {
                          // Touch long-press would otherwise open the browser menu.
                          if (longPressFired.current) e.preventDefault();
                        }}
                        onClick={() => {
                          if (draggingId) return;
                          if (longPressFired.current) {
                            longPressFired.current = false;
                            return;
                          }
                          onSelectSession(session.id);
                        }}
                        className={`flex items-center gap-2 rounded-xl border border-base-300 bg-base-100 px-3 py-2.5 transition-colors hover:bg-base-200 select-none ${
                          movable ? "cursor-grab" : "cursor-pointer"
                        } ${draggingId === session.id ? "opacity-50" : ""}`}
                        style={{ WebkitTouchCallout: "none" }}
                        title={
                          movable
                            ? "Drag or long-press to move to another day"
                            : undefined
                        }
                      >
                        <span
                          className="h-2 w-2 flex-shrink-0 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="flex-1 truncate text-sm font-medium text-base-content font-grotesk">
                          {session.title}
                        </span>
                        {session.timeLabel && !session.isLive && (
                          <span className="text-sm text-base-content/50 font-geist tabular-nums">
                            {session.timeLabel}
                          </span>
                        )}
                        <SessionStateChip session={session} />
                        {movable && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onChangeDate(session.id);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="flex h-11 w-11 -my-2 -mr-2 items-center justify-center rounded-xl text-base-content/40 hover:text-base-content hover:bg-base-300 transition-colors"
                            aria-label="Change date"
                          >
                            <HiCalendarDays className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekView;
